import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";

import { ApiFrdFrdsListRequest, FRD } from "@nens/brostar/openapi-client";

import { getApi } from "@/api/api-configuration";

import { findInListQuery } from "./helpers/find-in-list-query";
import { simplifyQueryParam } from "./helpers/simplify-query-key";

const uploadApi = getApi();

// ─── SINGLE ──────────────────────────────────────────────────────────────────

const getFrdSingleQuery = (uuid: string) => ({
  queryKey: ["frd-single", uuid],
  queryFn: () => uploadApi.apiFrdFrdsRead({ uuid }),
});

export const useFrdSingleQueryDirectly = () => {
  const queryClient = useQueryClient();
  return (uuid: string) =>
    findInListQuery<FRD>(queryClient, ["frd-list"], uuid, getFrdSingleQuery(uuid));
};

// ─── LIST ─────────────────────────────────────────────────────────────────────

const getFrdListQuery = (options: ApiFrdFrdsListRequest) => ({
  queryKey: ["frd-list", simplifyQueryParam(options)],
  queryFn: () => uploadApi.apiFrdFrdsList(options),
  placeholderData: keepPreviousData,
});

export const useFrdListQuery = (options: ApiFrdFrdsListRequest) => {
  return useQuery(getFrdListQuery(options));
};

// ─── INVALIDATION ─────────────────────────────────────────────────────────────

export const useInvalidateFrdQueries = () => {
  const queryClient = useQueryClient();
  return useCallback(async () => {
    await queryClient.invalidateQueries({
      queryKey: ["frd-single"],
      refetchType: "all",
    });
    await queryClient.invalidateQueries({
      queryKey: getFrdListQuery({}).queryKey.slice(0, -1),
      refetchType: "all",
    });
  }, [queryClient]);
};
